import { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useTransactionValidationGuard } from '../../../hooks/useTransactionValidationGuard';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import useLogError from '../../../hooks/useLogError';
import {
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Item, ItemRequest, Reason, WasteCheckSheetRequest, WasteCheckSheetResponse, WasteItemUomInfo } from '../../../app/services/api/generated';
import { ExtendedGetItemsResponse } from '../interfaces/ExtendedGetItemsResponse';
import { ItemWasteChecksheetData, SaveStatus, WasteLogReason, DEFAULT_WASTE_REASONS, POSITION_TO_QTY_FIELD, POSITION_TO_UOM_FIELD } from '../interfaces/ItemWasteChecksheetData';
import { ItemPreview } from '../interfaces/ItemsPreview';
import { Category } from '../interfaces/Category';
import { ConfigurationName } from '../../../enums/ConfigurationName';
import { getAvailableUoms } from '../utils/uomConversionUtils';
import { buildPrintDocument } from '../print/printTemplates';
import { useConfigurations } from '../../../components/shared/useConfigurations';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { getCurrencySymbol } from '../../../utils';

export const useWasteChecksheetDetail = () => {
  const { getItems, getWasteById, getWasteLogHeaders, getWasteItemConversions } = useNswagClient();
  const { isConfigurationEnabled } = useConfigurations();
  const { currencyCode } = useCurrencyCodeConfiguration();
  const [itemsData, setItemsData] = useState<ExtendedGetItemsResponse>({});
  const [wasteLogReasons, setWasteLogReasons] = useState<WasteLogReason[]>([]);
  const isWasteLogUnitSupportEnabled = isConfigurationEnabled(ConfigurationName.WasteLogUnitSupport);
  const [itemConversions, setItemConversions] = useState<Record<string, WasteItemUomInfo[]>>({});
  const currencySymbol = useMemo(() => getCurrencySymbol(currencyCode) || '$', [currencyCode]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showSubmitAlert, setShowSubmitAlert] = useState(false);
  const { id } = useParams<{ id: string | undefined }>();
  const parsedId = id ? parseInt(id, 10) : 0;

  const theme = useTheme();
  const { selectedStore } = useContext(UserContext);

  const [isArchived, setIsArchived] = useState<boolean>(false);
  const [submittedBy, setSubmittedBy] = useState<string>('');
  const [submittedDate, setSubmittedDate] = useState<string | undefined>('');

  const [showSavedAlert, setShowSavedAlert] = useState(false);
  const [showErrorSavedAlert, setShowErrorSavedAlert] = useState(false);
  const [showErrorSubmitAlert, setShowErrorSubmitAlert] = useState(false);
  const [openSubmitModal, setOpenSubmitModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemsByCategory, setItemsByCategory] = useState<Category[]>([]);
  const [itemsPreviewOnSubmitModal, setItemsPreviewOnSubmitModal] = useState<ItemPreview[]>([]);
  const [itemsRequest, setItemsRequest] = useState<ItemRequest[]>([]);
  const { hasPermissionTo, isAdmin } = useContext(UserContext);
  const hasPermissionToWrite = hasPermissionTo(['DailyWasteCheckSheetWrite']) || isAdmin();
  const [modifiedItems, setModifiedItems] = useState<Set<number>>(new Set());
  const [lastFocusedItemId, setLastFocusedItemId] = useState<number | null>(null);
  const [inactivityTimer, setInactivityTimer] = useState<NodeJS.Timeout | null>(null);
  const [navigateToIndex, setNavigateToIndex] = useState<boolean>(false);

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.lg}px)`);

  const { logError } = useLogError();

  const navigate = useNavigate();

  const INACTIVITY_TIMEOUT = 60000; // 1 min on milliseconds

  const { createWaste, submitWaste, validateTransaction } = useNswagClient();
  const { runGuard, transactionValidationDialog } = useTransactionValidationGuard();
  const blocker = useBlocker(() => modifiedItems.size > 0);

  const { t } = useTranslation('wasteChecksheet');

  // Search & filter state (only used when isWasteLogUnitSupportEnabled)
  const [searchText, setSearchText] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const allCategoryNames = useMemo(() => {
    return itemsByCategory.map(c => c.name);
  }, [itemsByCategory]);

  // Cache the "no filter" wrapped array to avoid creating new objects every render.
  // This keeps React.memo on WasteChecksheetTable effective when filters are cleared.
  const unfilteredCacheRef = useRef<{ src: Category[]; result: { category: Category; originalCategoryIndex: number }[] }>({ src: [], result: [] });

  const filteredItemsByCategory = useMemo((): { category: Category; originalCategoryIndex: number }[] => {
    const noFilter = !searchText.trim() && selectedCategories.length === 0;

    // Fast-path: no filtering needed – return a stable reference
    if (!isWasteLogUnitSupportEnabled || noFilter) {
      if (unfilteredCacheRef.current.src === itemsByCategory) {
        return unfilteredCacheRef.current.result;
      }
      const result = itemsByCategory.map((category, index) => ({ category, originalCategoryIndex: index }));
      unfilteredCacheRef.current = { src: itemsByCategory, result };
      return result;
    }

    const lowerSearch = searchText.toLowerCase().trim();
    const result: { category: Category; originalCategoryIndex: number }[] = [];

    itemsByCategory.forEach((category, originalIndex) => {
      const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(category.name);
      if (!matchesCategory) return;

      if (!lowerSearch) {
        result.push({ category, originalCategoryIndex: originalIndex });
        return;
      }

      const filteredItems = category.items.filter(item => {
        const itemNumber = (item.itemNumber ?? '').toLowerCase();
        const description = (item.description ?? '').toLowerCase();
        return itemNumber.includes(lowerSearch) || description.includes(lowerSearch);
      });

      if (filteredItems.length > 0) {
        result.push({ category: { ...category, items: filteredItems }, originalCategoryIndex: originalIndex });
      }
    });

    return result;
  }, [itemsByCategory, searchText, selectedCategories, isWasteLogUnitSupportEnabled]);

  const hasRecentActivity = true;
  const isDailyWaste = true;
  const sort = 'ItemNumber';

  const isEditMode = !!(id && id !== '0');

  useEffect(() => {
    loadData();
  }, [selectedStore]);

  useEffect(() => {
    if (modifiedItems.size > 0 && isEditMode) {
      startInactivityTimer();
    }

    const beforeUnload = (event: { preventDefault: () => void; returnValue: string; }) => {
      if (modifiedItems.size > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [modifiedItems]);

  useEffect(() => {
    if (navigateToIndex) {
      navigate('/store/waste-logs');
      if (blocker?.state === 'blocked') {
        blocker.proceed();
      }
      setNavigateToIndex(false);
    }
  }, [navigateToIndex, blocker]);

  const PAGE_SIZE = 25;

  const fetchAllItems = async (storeNumber: string): Promise<ExtendedGetItemsResponse> => {
    const allItems: Item[] = [];

    const firstResult = await getItems(
      undefined, storeNumber, hasRecentActivity, undefined, undefined,
      undefined, undefined, undefined, undefined, isDailyWaste, sort,
      undefined, 0, PAGE_SIZE,
    );

    const totalCount = firstResult?.data?.itemCount ?? 0;
    allItems.push(...(firstResult?.data?.items ?? []));

    while (allItems.length < totalCount) {
      const result = await getItems(
        undefined, storeNumber, hasRecentActivity, undefined, undefined,
        undefined, undefined, undefined, undefined, isDailyWaste, sort,
        undefined, allItems.length, PAGE_SIZE,
      );

      const pageItems = result?.data?.items ?? [];
      if (pageItems.length === 0) break; 
      allItems.push(...pageItems);
    }

    return { itemCount: totalCount, items: allItems as ItemWasteChecksheetData[] };
  };

  const loadData = async () => {
    const storeNumber = selectedStore?.storeNumber;
    if (!storeNumber) return;
    
    setIsLoading(true);

    try {
      const [itemsResult, wasteResult, wasteLogHeadersResult] = await Promise.all([
        fetchAllItems(storeNumber),
        isEditMode && id ? getWasteById(parseInt(id, 10)) : Promise.resolve(null),
        getWasteLogHeaders(storeNumber),
      ]);

      // Check if this is an existing waste log (has data from API)
      const wasteHeaders = wasteResult?.data?.wasteHeaders;
      const isExistingWasteLog = wasteHeaders?.id != null;
      
      // Check if existing waste log has persisted reason names
      const hasPersistedReasons = wasteHeaders?.reason1Name || wasteHeaders?.reason2Name || 
                                   wasteHeaders?.reason3Name || wasteHeaders?.reason4Name;

      if (hasPersistedReasons) {
        const persistedReasons: WasteLogReason[] = ([1, 2, 3, 4] as const)
          .map((num) => ({
            id: num - 1,
            name: wasteHeaders[`reason${num}Name` as keyof typeof wasteHeaders] as string,
          }))
          .filter((reason) => !!reason.name);
        setWasteLogReasons(persistedReasons);
      } else if (isExistingWasteLog) {
        setWasteLogReasons(DEFAULT_WASTE_REASONS);
      } else if (wasteLogHeadersResult?.data?.reasons && wasteLogHeadersResult.data.reasons.length > 0) {
        const reasons: WasteLogReason[] = wasteLogHeadersResult.data.reasons
          .filter((r: Reason) => r.id && r.name)
          .map((r: Reason) => ({
            id: r.id!,
            name: r.name!,
            description: r.description,
            wasteLogSortOrder: r.wasteLogSortOrder,
          }))
          .sort((a: WasteLogReason, b: WasteLogReason) => {
            const sortOrderA = a.wasteLogSortOrder ?? Number.MAX_SAFE_INTEGER;
            const sortOrderB = b.wasteLogSortOrder ?? Number.MAX_SAFE_INTEGER;
            if (sortOrderA !== sortOrderB) {
              return sortOrderA - sortOrderB;
            }
            return a.name.localeCompare(b.name);
          });
        setWasteLogReasons(reasons);
      } else {
        setWasteLogReasons(DEFAULT_WASTE_REASONS);
      }

      // UOM enrichment is isolated — if it fails, items still load without UOM data
      let conversionsMap: Record<string, WasteItemUomInfo[]> = {};
      if (isWasteLogUnitSupportEnabled) {
        try {
          const conversionsResult = await getWasteItemConversions(storeNumber);
          conversionsMap = conversionsResult?.data?.itemConversions ?? {};
          setItemConversions(conversionsMap);
        } catch (e) {
          logError(e);
        }
      }

      // Always set items data — enrich with UOM only if available
      let finalItemsResult = itemsResult;
      if (finalItemsResult) {
        if (isWasteLogUnitSupportEnabled && finalItemsResult.items) {
          finalItemsResult = {
            ...finalItemsResult,
            items: finalItemsResult.items.map((item) => ({
              ...item,
              availableUoms: getAvailableUoms(
                item.uom ?? '',
                conversionsMap[item.itemNumber ?? ''],
              ),
            })),
          };
        }

        // Handle waste data
        if (wasteResult?.data) {
          const mappedItems = mapWasteDataToItems(wasteResult.data, finalItemsResult);
          if (mappedItems) {
            finalItemsResult = { ...finalItemsResult, items: mappedItems };
          }
        }

        // Set items and computed categories together to avoid double render
        setItemsData(finalItemsResult);
        setItemsByCategory(groupCategoryItems(finalItemsResult));
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const mapWasteDataToItems = (wasteData: WasteCheckSheetResponse, itemsDataToBeMapped: ExtendedGetItemsResponse) => {
    const wasteItems = wasteData?.wasteHeaders?.items ?? [];
    if (wasteData.wasteHeaders?.submittedBy) {
      setIsArchived(true);
      setSubmittedDate(wasteData.wasteHeaders?.submittedDate);
      setSubmittedBy(wasteData.wasteHeaders?.submittedBy);
    }
    const wasteItemsMap = new Map(wasteItems.map((item) => [item.itemId, item]));

    return itemsDataToBeMapped?.items?.map((item) => {
      const wasteItem = wasteItemsMap.get(item.id);
      if (wasteItem) {
        return {
          ...item,
          pastDueQty: wasteItem.pastDueQty,
          sampledQty: wasteItem.sampledQty,
          damagedQty: wasteItem.damagedQty,
          storeUseQty: wasteItem.storeUseQty,
          pastDueQtyUom: wasteItem.pastDueQtyUom ?? undefined,
          sampledQtyUom: wasteItem.sampledQtyUom ?? undefined,
          damagedQtyUom: wasteItem.damagedQtyUom ?? undefined,
          storeUseQtyUom: wasteItem.storeUseQtyUom ?? undefined,
        };
      }
      return item;
    });
  };

  const handleLeavePageModalCancel = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset();
    }
  };

  const handleLeavePageModalConfirm = () => {
    if (isSaveSubmitEnabled()) {
      handleSaveConfirmModal();
    }
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const startInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    const timer = setTimeout(() => {
      if (modifiedItems.size > 0) {
        modifiedItems.forEach(itemId => {
          autosaveItem(itemId);
        });
        setIsDirty(false);
      }
    }, INACTIVITY_TIMEOUT);
    setInactivityTimer(timer);
  };

  const handleItemFocus = (itemId: number) => {
    if (lastFocusedItemId !== null && lastFocusedItemId !== itemId && modifiedItems.has(lastFocusedItemId)) {
      autosaveItem(lastFocusedItemId);
      setIsDirty(false);
    }
    setLastFocusedItemId(itemId);
  };

  //takes each item and changes the status from standard, idle, saving, saved. when the itemId matches the item
  //when the itemId is undefined it does all items, fromStatus is undefined then for all current status
  const updateItemStatus = (items: ItemWasteChecksheetData[], itemId: number | undefined, fromStatus: SaveStatus | undefined, toStatus: SaveStatus): ItemWasteChecksheetData[] => {
    const updateStatus = (currentStatus: SaveStatus | undefined): SaveStatus =>
      fromStatus === undefined || currentStatus === fromStatus ? toStatus : currentStatus ?? 'standard';

    return items.map(item =>
      itemId === undefined || item.id === itemId
        ? {
          ...item,
          pastDueQtySaveStatus: updateStatus(item.pastDueQtySaveStatus),
          sampledQtySaveStatus: updateStatus(item.sampledQtySaveStatus),
          damagedQtySaveStatus: updateStatus(item.damagedQtySaveStatus),
          storeUseQtySaveStatus: updateStatus(item.storeUseQtySaveStatus),
        }
        : item,
    );
  };

  const updateCategoryItems = (itemId: number | undefined, fromStatus: SaveStatus | undefined, toStatus: SaveStatus) => {
    if (parsedId != 0 || parsedId != undefined) {
      setItemsByCategory(prev => prev.map(category => ({
        ...category,
        items: updateItemStatus(category.items, itemId, fromStatus, toStatus),
      })));
    }
  };

  const autosaveItem = async (itemId: number) => {
    if (!itemId) return;

    updateCategoryItems(itemId, 'idle', 'saving');

    const requestData: WasteCheckSheetRequest = {
      id: parsedId,
      storeNumber: selectedStore?.storeNumber ?? '',
      items: flattenAndFilterItems(),
    };

    try {
      await createWaste(requestData);

      setModifiedItems(prev => {
        const newModifiedItems = new Set(prev);
        newModifiedItems.delete(itemId);
        return newModifiedItems;
      });

      setTimeout(() => updateCategoryItems(itemId, 'saving', 'saved'), 2000);
      setTimeout(() => updateCategoryItems(itemId, 'saved', 'standard'), 5000);

    } catch (error) {
      setShowErrorSavedAlert(true);
      logError(error);
      updateCategoryItems(itemId, 'saving', 'error');
    }
  };

  const groupCategoryItems = (itemsData: ExtendedGetItemsResponse): Category[] => {
    if (!itemsData?.items?.length) {
      return [];
    }

    const categories: { [key: string]: Category } = {};

    for (const item of itemsData.items) {
      const category = item.primaryCategory?.replace(/\s+/g, ' ').trim().toUpperCase();
      if (category) {
        categories[category] = categories[category] ?? { name: category, items: [] };
        categories[category].items.push(item);
      }
    }

    const sortedCategories = Object.values(categories).sort((a, b) => a.name.localeCompare(b.name));
    return sortedCategories;
  };

  const resetItemQuantities = (category: Category) => {
    return category.items.map(item => ({
      ...item,
      pastDueQty: undefined,
      sampledQty: undefined,
      damagedQty: undefined,
      storeUseQty: undefined,
    }));
  };

  const resetCategoryItems = (prevItemsByCategory: Category[]) => {
    return prevItemsByCategory.map(category => ({
      ...category,
      items: resetItemQuantities(category),
    }));
  };

  const handleReset = () => {
    setItemsByCategory(prevItemsByCategory => resetCategoryItems(prevItemsByCategory));
    updateCategoryItems(undefined, undefined, 'standard');
  };

  const flattenAndFilterItems = (): ItemRequest[] => {
    const flattenItems = itemsByCategory.flatMap(category => category.items);

    const filteredItems: ItemRequest[] = flattenItems.filter(
      item =>
        (item.pastDueQty ?? 0) > 0 ||
        (item.sampledQty ?? 0) > 0 ||
        (item.damagedQty ?? 0) > 0 ||
        (item.storeUseQty ?? 0) > 0,
    ).map((item) => ({
      id: item.id ?? 0,
      itemNumber: item.itemNumber ?? '',
      description: item.description ?? '',
      uom: item.uom ?? '',
      pastDueQty: item.pastDueQty ?? 0,
      sampledQty: item.sampledQty ?? 0,
      damagedQty: item.damagedQty ?? 0,
      storeUseQty: item.storeUseQty ?? 0,
      pastDueQtyUom: item.pastDueQtyUom,
      sampledQtyUom: item.sampledQtyUom,
      damagedQtyUom: item.damagedQtyUom,
      storeUseQtyUom: item.storeUseQtyUom,
    }));

    setItemsRequest(filteredItems);
    return filteredItems;
  };

  const createPreviewItems = (itemsRequest: ItemRequest[]): ItemPreview[] => {
    const previewItems: ItemPreview[] = [];

    // Map legacy field names to configured reason names by position
    const fieldToReason: Record<string, string> = {};
    const legacyFields = Object.values(POSITION_TO_QTY_FIELD);
    
    if (wasteLogReasons.length > 0) {
      // Use configured reasons
      wasteLogReasons.forEach((reason, index) => {
        if (index < legacyFields.length) {
          fieldToReason[legacyFields[index]] = reason.name;
        }
      });
    } else {
      // Fallback to default translated names
      legacyFields.forEach(field => {
        fieldToReason[field] = t(field);
      });
    }

    for (const item of itemsRequest) {
      for (let i = 0; i < legacyFields.length; i++) {
        const field = legacyFields[i];
        const type = field as keyof ItemRequest;
        const qty = (item[type] as number | undefined) ?? 0;
        if (qty > 0) {
          const reason = fieldToReason[field] ?? t(field);
          const uomField = POSITION_TO_UOM_FIELD[i] as keyof ItemRequest | undefined;
          const uom = uomField ? (item[uomField] as string | undefined) : undefined;
          previewItems.push({
            itemNumber: item.itemNumber,
            description: item.description ? item.description : '',
            qty: qty,
            type: reason,
            uom: uom ?? item.uom,
          });
        }
      }
    }

    return previewItems;
  };

  const isSaveSubmitEnabled = () => {
    const someNonZeroValue = itemsByCategory.flatMap(category => category.items).some(
      item =>
        (item.pastDueQty ?? 0) > 0 ||
        (item.sampledQty ?? 0) > 0 ||
        (item.damagedQty ?? 0) > 0 ||
        (item.storeUseQty ?? 0) > 0);

    if (itemsByCategory.length === 0 || isSaving || !isDirty || !someNonZeroValue) return false;

    return true;
  };

  const handleSubmitButton = () => {
    const filteredItems = flattenAndFilterItems();
    const previewItems = createPreviewItems(filteredItems);
    setItemsPreviewOnSubmitModal(previewItems);
    setOpenSubmitModal(true);
  };

  const handleConfirmSubmitModal = () => {
    setOpenSubmitModal(false);

    const requestData: WasteCheckSheetRequest = {
      id: parsedId,
      storeNumber: selectedStore?.storeNumber ?? '',
      items: itemsRequest,
    };
    const itemNumbers = (itemsRequest ?? []).map(i => i.itemNumber ?? '').filter(Boolean);

    const doSubmit = () => {
      setIsSubmitting(true);
      setIsDirty(false);
      submitWaste(requestData)
        .then(() => {
          loadData();
          setIsSubmitting(false);
          setShowSubmitAlert(true);
        })
        .catch((error) => {
          setShowErrorSubmitAlert(true);
          setIsSubmitting(false);
          logError(error);
        });
    };

    runGuard(
      () => validateTransaction(selectedStore?.storeNumber ?? '', dayjs().format('YYYY-MM-DDTHH:mm:ss'), itemNumbers),
      doSubmit,
    );
  };

  const handleSubmitCancelModal = () => {
    setIsSubmitting(false);
    setOpenSubmitModal(false);
  };

  const handleSaveConfirmModal = () => {
    setIsSaving(true);
    setIsDirty(false);
    const filterItems = flattenAndFilterItems();

    const requestData: WasteCheckSheetRequest = {
      id: parsedId,
      storeNumber: selectedStore?.storeNumber ?? '',
      items: filterItems,
    };

    createWaste(requestData)
      .then(() => {
        setModifiedItems(new Set());
        setIsSaving(false);
        setShowSavedAlert(true);

        if (!isEditMode) {
          setTimeout(() => {
            handleAfterSave();
          }, 1000);
        }

        updateCategoryItems(undefined, undefined, 'standard');
      })
      .catch(error => {
        setIsSaving(false);
        setShowErrorSavedAlert(true);
        logError(error);
      });
  };

  const handleQuantityChange = useCallback((categoryIndex: number, itemIndex: number, field: keyof ItemWasteChecksheetData, value: string) => {
    let parsedValue = value === '' ? null : (Math.round(Number(value) * 1000) / 1000);
    if (parsedValue && parsedValue < 0) parsedValue = 0;

    setItemsByCategory(prev => prev.map((category, catIdx) => {
      if (catIdx !== categoryIndex) return category;
      return {
        ...category,
        items: category.items.map((item, iIdx) => {
          if (iIdx !== itemIndex) return item;
          const updated = { ...item };
          if (updated.id) {
            setModifiedItems(prevMod => new Set(prevMod).add(updated.id!));
            setIsDirty(true);
          }
          switch (field) {
            case 'pastDueQty':
              updated.pastDueQty = parsedValue ?? undefined;
              if (!['saving', 'saved'].includes(updated.pastDueQtySaveStatus ?? '')) updated.pastDueQtySaveStatus = 'idle';
              break;
            case 'sampledQty':
              updated.sampledQty = parsedValue ?? undefined;
              if (!['saving', 'saved'].includes(updated.sampledQtySaveStatus ?? '')) updated.sampledQtySaveStatus = 'idle';
              break;
            case 'damagedQty':
              updated.damagedQty = parsedValue ?? undefined;
              if (!['saving', 'saved'].includes(updated.damagedQtySaveStatus ?? '')) updated.damagedQtySaveStatus = 'idle';
              break;
            case 'storeUseQty':
              updated.storeUseQty = parsedValue ?? undefined;
              if (!['saving', 'saved'].includes(updated.storeUseQtySaveStatus ?? '')) updated.storeUseQtySaveStatus = 'idle';
              break;
            default:
              break;
          }
          return updated;
        }),
      };
    }));
  }, []);

  const handleUomChange = useCallback((categoryIndex: number, itemIndex: number, field: keyof ItemWasteChecksheetData, uom: string) => {
    setItemsByCategory(prev => prev.map((category, catIdx) => {
      if (catIdx !== categoryIndex) return category;
      return {
        ...category,
        items: category.items.map((item, iIdx) => {
          if (iIdx !== itemIndex) return item;
          const updated = { ...item, [field]: uom };
          if (updated.id) {
            setModifiedItems(prevMod => new Set(prevMod).add(updated.id!));
            setIsDirty(true);
          }
          return updated;
        }),
      };
    }));
  }, []);

  const handleAfterSave = () => {
    navigate('/store/waste-logs');
  };

  const printTableContainer = () => {
    const activeReasons = wasteLogReasons.length > 0 ? wasteLogReasons : DEFAULT_WASTE_REASONS;

    const printDoc = buildPrintDocument({
      categories: itemsByCategory,
      activeReasons,
      itemConversions,
      isUomMode: isWasteLogUnitSupportEnabled,
      translations: {
        itemNumber: t('details.itemNumber'),
        itemDescription: t('details.itemDescription'),
        lowestUom: t('details.lowestUom'),
        uomConversions: t('details.uomConversions'),
        total: t('details.total'),
      },
    });

    const printWindow = window.open('', '', 'left=0,top=0,width=1650,height=900,toolbar=0,scrollbars=0,status=0');
    if (!printWindow) return;

    printWindow.document.write(printDoc);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  return {
    transactionValidationDialog,
    t,
    isLoading,
    isDirty,
    showSubmitAlert,
    itemsData,
    itemsByCategory,
    isArchived,
    submittedBy,
    submittedDate,
    loadData,
    updateCategoryItems,
    handleLeavePageModalCancel,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    handleItemFocus,
    handleSaveConfirmModal,
    handleSubmitButton,
    handleSubmitCancelModal,
    handleReset,
    isSaving,
    isSubmitting,
    showSavedAlert,
    showErrorSavedAlert,
    showErrorSubmitAlert,
    openSubmitModal,
    setShowSavedAlert,
    setShowErrorSavedAlert,
    setShowErrorSubmitAlert,
    printTableContainer,
    hasPermissionToWrite,
    isMobile,
    isSaveSubmitEnabled,
    isEditMode,
    handleQuantityChange,
    handleUomChange,
    handleConfirmSubmitModal,
    itemsPreviewOnSubmitModal,
    modifiedItems,
    blocker,
    setShowSubmitAlert,
    wasteLogReasons,
    isWasteLogUnitSupportEnabled,
    itemConversions,
    currencySymbol,
    searchText,
    setSearchText,
    selectedCategories,
    setSelectedCategories,
    allCategoryNames,
    filteredItemsByCategory,
  };
};
